import clsx from 'clsx'
import { BaseModalProps, Modal, Tabs } from 'components/core-v2'
import { useState } from 'react'

import { CURRENCY } from '../../../../../const'
import { useFilteredGetByTenantAccountDashboardQuery } from '../../../../../modules/reporting/utils/useFilteredNotes'
import { formatCurrencyFull } from '../../../../../utilities/currency'
import { plural } from '../../../../../utilities/plural'
import classes from './index.module.scss'
import { Loading } from './Loading'
import { PortfoliosTab } from './portfolios-tab'
import { RebalancingTab } from './rebalancing-tab'
import { TaxClassificationTab } from './tax-classification-tab'
import { ModelAndFundOfFundsPortfolioType } from './types'

type Tab = 'portfolios' | 'rebalancing' | 'tax-classifications'
const Body = ({
  onClose,
  portType
}: Pick<BaseModalProps, 'onClose'> & { portType: ModelAndFundOfFundsPortfolioType }) => {
  const { data, isLoading } = useFilteredGetByTenantAccountDashboardQuery()
  const [selectedTab, setSelectedTab] = useState<Tab>('portfolios')

  if (isLoading) return <Loading portType={portType} onClose={onClose} />

  const { fundOfFunds, model } = data.portfolios
  const port = portType === 'ModelPortfolio' ? model : fundOfFunds

  const portfolios = port.portfolios.map(portfolio => ({
    ...portfolio,
    // This is a temporary measure due to Dominion trades not having a portfolio id or name
    modelPortfolioId: portfolio.portfolioId || 'dominion-growth-portfolio',
    modelPortfolioName: portfolio.portfolioName || 'Dominion Growth Portfolio'
  }))
  const totalValue = formatCurrencyFull(port.zarValue, CURRENCY)
  const portfolioCount = port.portfolios.length

  return (
    <>
      <Modal.Header id="model-portfolio-modal" onClose={onClose}>
        <h2>{portType === 'ModelPortfolio' ? 'Model' : 'Fund of funds'}</h2>
      </Modal.Header>
      <Modal.Body className={classes.body}>
        <div className={clsx(classes.totals, 'mb-8')}>
          <p className="mr-8">
            Total value: <strong>{totalValue}</strong>
          </p>
          <p>
            Total {plural('portfolio', portfolioCount)}: <strong>{portfolioCount}</strong>
          </p>
        </div>
        <Tabs.List ruleClassName="mb-4" spacing="md">
          <Tabs.Tab
            isSelected={selectedTab === 'portfolios'}
            value="portfolios"
            onClick={() => setSelectedTab('portfolios')}
          >
            Portfolios
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'rebalancing'}
            value="rebalancing"
            onClick={() => setSelectedTab('rebalancing')}
          >
            Rebalancing
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'tax-classifications'}
            value="tax-classifications"
            onClick={() => setSelectedTab('tax-classifications')}
          >
            Tax classifications
          </Tabs.Tab>
        </Tabs.List>
        {selectedTab === 'portfolios' && (
          <Tabs.Panel className="flex-1" value="portfolios">
            <PortfoliosTab portType={portType} portfolios={portfolios} />
          </Tabs.Panel>
        )}
        {selectedTab === 'rebalancing' && (
          <Tabs.Panel value="rebalancing">
            <RebalancingTab portfolios={portfolios} />
          </Tabs.Panel>
        )}
        {selectedTab === 'tax-classifications' && (
          <Tabs.Panel value="tax-classifications">
            <TaxClassificationTab portfolios={portfolios} />
          </Tabs.Panel>
        )}
      </Modal.Body>
    </>
  )
}

export const ModelPortfolioModal = ({ isOpen, onClose }: BaseModalProps) => (
  <Modal.Wrapper
    aria-labelledby="model-portfolio-modal"
    className={classes.wrapper}
    isOpen={isOpen}
    width="lg"
    onClose={onClose}
  >
    <Body portType="ModelPortfolio" onClose={onClose} />
  </Modal.Wrapper>
)

export const FofPortfolioModal = ({ isOpen, onClose }: BaseModalProps) => (
  <Modal.Wrapper
    aria-labelledby="fof-portfolio-modal"
    className={classes.wrapper}
    isOpen={isOpen}
    width="lg"
    onClose={onClose}
  >
    <Body portType="FundOfFunds" onClose={onClose} />
  </Modal.Wrapper>
)
